import {
  CheckboxCell,
  DateCell,
  FileCell,
  LeadTimeCell,
  LinkCell,
  LongTextCell,
  NumberCell,
  PersonCell,
  StatusCell,
  TagsCell,
  TextCell,
  TimelineCell,
} from "@/components/board/cells";
import type { LinkValue } from "@/components/board/cells/LinkCell";
import { isWorkflowBoard } from "@/lib/boardView";
import type { TimelineRange, ViewBoard, ViewColumn, ViewRow } from "@/lib/boardView";
import { isWorkflowStatusColumn } from "@/lib/statusRoles";
import type { CellValue, FileRef, LeadTimeValue, MentionItem, UUID } from "@/lib/types";

/**
 * Renders the right editor for one board column inside a detail view (panel or
 * mobile sheet), bound to the row's RAW cell value and writing via onPatchCell.
 * Shared by RowDetailPanel + RowDetailSheet so both show every real column with
 * an editable, type-correct field — no fixture fields, no flat-ViewRow aliasing.
 */
export function DetailColumnEditor({
  column,
  row,
  board,
  onPatchCell,
  mentionItems,
  readOnly = false,
}: {
  column: ViewColumn;
  row: ViewRow;
  board: ViewBoard;
  onPatchCell?: (rowId: string, columnId: string, value: CellValue) => void;
  mentionItems?: MentionItem[];
  /** Force this field read-only regardless of board write access — e.g. a
   * ticket's requester Beschreibung shown to staff who are not the creator. */
  readOnly?: boolean;
}) {
  const raw = row.raw?.cells?.find((c) => c.column_def_id === column.id)?.value;
  const patch = (v: CellValue) => onPatchCell?.(row.id, column.id, v);

  switch (column.type) {
    case "text":
      return (
        <TextCell
          value={typeof raw === "string" ? raw : ""}
          sticky={false}
          openable={false}
          onPatch={(v) => patch(v)}
        />
      );
    case "long_text":
      return (
        <LongTextCell
          value={typeof raw === "string" ? raw : ""}
          expanded
          readOnly={readOnly}
          onPatch={readOnly ? undefined : (v) => patch(v)}
          mentionItems={mentionItems}
        />
      );
    case "status":
      return (
        <StatusCell
          value={typeof raw === "string" ? raw : null}
          options={column.options ?? []}
          pillStyle="tint"
          readOnly={isWorkflowBoard(board) && isWorkflowStatusColumn(column)}
          onChange={(v) => patch(v)}
        />
      );
    case "person":
      return (
        <PersonCell
          value={Array.isArray(raw) ? (raw as string[]) : []}
          workspaceId={board.workspace_id as UUID}
          boardId={board.id as UUID}
          allowMultiple={(column.raw?.config?.allow_multiple as boolean | undefined) ?? true}
          onChange={(v) => patch(v)}
        />
      );
    case "date":
      return <DateCell value={typeof raw === "string" ? raw : null} onPatch={(v) => patch(v)} />;
    case "timeline":
      return (
        <TimelineCell value={(raw as TimelineRange | null) ?? null} onPatch={(v) => patch(v)} />
      );
    case "number":
      return (
        <NumberCell
          value={typeof raw === "number" ? raw : null}
          column={column}
          onPatch={(v) => patch(v)}
        />
      );
    case "tags":
      return (
        <TagsCell
          value={Array.isArray(raw) ? (raw as string[]) : []}
          options={column.options}
          onPatch={(v) => patch(v)}
        />
      );
    case "link":
      return <LinkCell value={(raw as LinkValue | null) ?? null} onPatch={(v) => patch(v)} />;
    case "checkbox":
      return <CheckboxCell value={raw === true} onChange={(v) => patch(v)} align="flex-start" />;
    case "lead_time":
      return (
        <LeadTimeCell
          value={
            raw && typeof raw === "object" && !Array.isArray(raw) && "unit" in raw
              ? (raw as LeadTimeValue)
              : null
          }
          goalDate={board.goalDate}
          rowH={44}
          onPatch={(v) => patch(v)}
        />
      );
    case "file":
      return (
        <FileCell
          variant="detail"
          value={Array.isArray(raw) ? (raw as FileRef[]) : []}
          {...(readOnly ? {} : { onPatch: (v: FileRef[]) => patch(v) })}
        />
      );
    // Read-only ticket meta — same values the table cells show (keep the two
    // renderers in sync). Computed/snapshotted, never editable here.
    case "creator":
      return (
        <TextCell
          value={row.raw?.created_by_display_name ?? row.raw?.created_by_username ?? "—"}
          sticky={false}
          openable={false}
        />
      );
    case "creator_department":
      return (
        <TextCell value={row.raw?.creator_department_name ?? "—"} sticky={false} openable={false} />
      );
    case "creator_company":
      return (
        <TextCell value={row.raw?.creator_company_name ?? "—"} sticky={false} openable={false} />
      );
    default:
      return null;
  }
}
